/**
 * Admin List Pages - Refactored
 * Thin route components using the generic AdminListPage system
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Users, Palette, FileText, Image, Flag, BookOpen, 
  History, Shield, Ban, CheckCircle, XCircle, Clock,
  Eye, Edit, Trash2, MoreVertical, AlertTriangle
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  AdminPageShell,
  AdminTable,
  AdminPagination,
  AdminFilters,
  AdminEmptyState,
  AdminActionMenu,
  useAdminList,
  useAdminAction,
  adminPost,
} from '../admin/components';

// ===========================
// ADMIN USERS LIST
// ===========================
export const AdminUsersList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { execute: executeAction, loading: actionLoading } = useAdminAction();

  const {
    data: users,
    total,
    loading,
    error,
    isAuthError,
    page,
    limit,
    totalPages,
    setPage,
    setLimit,
    setFilter,
    params,
    refresh,
    isEmpty,
  } = useAdminList('users', { status: '', role: '' });

  const handleBan = async (userId, reason) => {
    if (!reason) {
      reason = prompt('Enter ban reason:');
      if (!reason) return;
    }
    const result = await adminPost(`/admin/users/${userId}/ban`, { reason });
    if (result.ok) {
      toast.success('User banned');
      refresh();
    } else {
      toast.error(result.error);
    }
  };

  const handleUnban = async (userId) => {
    const result = await adminPost(`/admin/users/${userId}/unban`, {});
    if (result.ok) {
      toast.success('User unbanned');
      refresh();
    } else {
      toast.error(result.error);
    }
  };

  const columns = [
    {
      key: 'user',
      header: 'User',
      render: (row) => (
        <div className="flex items-center gap-3">
          {row.avatar_url ? (
            <img src={row.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
              <Users className="w-5 h-5 text-zinc-500" />
            </div>
          )}
          <div>
            <div className="font-medium text-white">{row.display_name || row.email}</div>
            <div className="text-sm text-zinc-400">{row.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (row) => (
        <Badge variant="outline" className={
          row.role === 'admin' ? 'bg-red-500/20 text-red-400 border-red-500/50' :
          row.role === 'creator' ? 'bg-purple-500/20 text-purple-400 border-purple-500/50' :
          'bg-zinc-500/20 text-zinc-400 border-zinc-500/50'
        }>
          {row.role || 'user'}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant="outline" className={
          row.status === 'banned' ? 'bg-red-500/20 text-red-400 border-red-500/50' :
          row.status === 'suspended' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' :
          'bg-green-500/20 text-green-400 border-green-500/50'
        }>
          {row.status || 'active'}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      header: 'Joined',
      render: (row) => (
        <span className="text-zinc-400 text-sm">
          {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      render: (row) => (
        <AdminActionMenu
          actions={[
            { label: 'View Details', icon: Eye, onClick: () => navigate(`/admin/users/${row.id}`) },
            row.status === 'banned' 
              ? { label: 'Unban User', icon: CheckCircle, onClick: () => handleUnban(row.id) }
              : { label: 'Ban User', icon: Ban, variant: 'danger', onClick: () => handleBan(row.id) },
          ]}
        />
      ),
    },
  ];

  const filters = [
    {
      key: 'role',
      options: [
        { value: 'all', label: 'All Roles' },
        { value: 'admin', label: 'Admins' },
        { value: 'creator', label: 'Creators' },
        { value: 'basic_user', label: 'Basic Users' },
      ],
    },
    {
      key: 'status',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'active', label: 'Active' },
        { value: 'suspended', label: 'Suspended' },
        { value: 'banned', label: 'Banned' },
      ],
    },
  ];

  return (
    <AdminPageShell
      title="User Management"
      subtitle={`${total} total users`}
      icon={Users}
      headerColor="from-blue-600 to-cyan-600"
      loading={loading && users.length === 0}
      error={error}
      isAuthError={isAuthError}
      onRetry={refresh}
    >
      <div className="space-y-6">
        <AdminFilters
          filters={filters}
          values={{ role: params.role || 'all', status: params.status || 'all' }}
          onChange={(key, val) => setFilter(key, val === 'all' ? '' : val)}
          searchValue={search}
          onSearchChange={setSearch}
          onSearch={() => setFilter('search', search)}
          searchPlaceholder="Search users..."
        />

        {isEmpty ? (
          <AdminEmptyState
            title="No users found"
            message={params.search ? `No users match "${params.search}"` : "No users to display."}
            icon={Users}
          />
        ) : (
          <>
            <AdminTable
              columns={columns}
              data={users}
              onRowClick={(row) => navigate(`/admin/users/${row.id}`)}
            />
            {totalPages > 1 && (
              <AdminPagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            )}
          </>
        )}
      </div>
    </AdminPageShell>
  );
};

// ===========================
// ADMIN CREATORS LIST
// ===========================
export const AdminCreatorsList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const {
    data: creators,
    total,
    loading,
    error,
    isAuthError,
    page,
    limit,
    totalPages,
    setPage,
    setLimit,
    setFilter,
    params,
    refresh,
    isEmpty,
  } = useAdminList('creators', { verified: '' });

  const columns = [
    {
      key: 'creator',
      header: 'Creator',
      render: (row) => (
        <div className="flex items-center gap-3">
          {row.avatar_url ? (
            <img src={row.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
              <Palette className="w-5 h-5 text-zinc-500" />
            </div>
          )}
          <div>
            <div className="font-medium text-white">{row.display_name}</div>
            <div className="text-sm text-zinc-400">@{row.handle}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'verified',
      header: 'Verified',
      render: (row) => row.is_verified ? (
        <CheckCircle className="w-5 h-5 text-green-400" />
      ) : (
        <XCircle className="w-5 h-5 text-zinc-500" />
      ),
    },
    {
      key: 'stats',
      header: 'Content',
      render: (row) => (
        <div className="text-sm text-zinc-400">
          {row.series_count || 0} series • {row.artwork_count || 0} artworks
        </div>
      ),
    },
    {
      key: 'followers',
      header: 'Followers',
      render: (row) => <span className="text-zinc-300">{row.followers_count || 0}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      render: (row) => (
        <AdminActionMenu
          actions={[
            { label: 'View Profile', icon: Eye, onClick: () => navigate(`/admin/creators/${row.user_id}`) },
            { label: 'View Content', icon: FileText, onClick: () => navigate(`/admin/series?creator=${row.user_id}`) },
          ]}
        />
      ),
    },
  ];

  const filters = [
    {
      key: 'verified',
      options: [
        { value: 'all', label: 'All Creators' },
        { value: 'true', label: 'Verified' },
        { value: 'false', label: 'Not Verified' },
      ],
    },
  ];

  return (
    <AdminPageShell
      title="Creator Management"
      subtitle={`${total} creators`}
      icon={Palette}
      headerColor="from-purple-600 to-pink-600"
      loading={loading && creators.length === 0}
      error={error}
      isAuthError={isAuthError}
      onRetry={refresh}
    >
      <div className="space-y-6">
        <AdminFilters
          filters={filters}
          values={{ verified: params.verified || 'all' }}
          onChange={(key, val) => setFilter(key, val === 'all' ? '' : val)}
          searchValue={search}
          onSearchChange={setSearch}
          onSearch={() => setFilter('search', search)}
          searchPlaceholder="Search creators..."
        />

        {isEmpty ? (
          <AdminEmptyState
            title="No creators found"
            message="No creators to display."
            icon={Palette}
          />
        ) : (
          <>
            <AdminTable
              columns={columns}
              data={creators}
              onRowClick={(row) => navigate(`/admin/creators/${row.user_id}`)}
            />
            {totalPages > 1 && (
              <AdminPagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            )}
          </>
        )}
      </div>
    </AdminPageShell>
  );
};

// ===========================
// ADMIN SERIES LIST
// ===========================
export const AdminSeriesList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const {
    data: series,
    total,
    loading,
    error,
    isAuthError,
    page,
    limit,
    totalPages,
    setPage,
    setLimit,
    setFilter,
    params,
    refresh,
    isEmpty,
  } = useAdminList('series', { status: '' });

  const columns = [
    {
      key: 'series',
      header: 'Series',
      render: (row) => (
        <div className="flex items-center gap-3">
          {row.cover_image_url ? (
            <img src={row.cover_image_url} alt="" className="w-12 h-16 rounded object-cover" />
          ) : (
            <div className="w-12 h-16 rounded bg-zinc-700 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-zinc-500" />
            </div>
          )}
          <div>
            <div className="font-medium text-white">{row.title}</div>
            <div className="text-sm text-zinc-400">by {row.creator_name || 'Unknown'}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant="outline" className={
          row.status === 'published' ? 'bg-green-500/20 text-green-400 border-green-500/50' :
          row.status === 'draft' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' :
          row.status === 'taken_down' ? 'bg-red-500/20 text-red-400 border-red-500/50' :
          'bg-zinc-500/20 text-zinc-400 border-zinc-500/50'
        }>
          {row.status || 'draft'}
        </Badge>
      ),
    },
    {
      key: 'chapters',
      header: 'Chapters',
      render: (row) => <span className="text-zinc-300">{row.chapter_count || 0}</span>,
    },
    {
      key: 'views',
      header: 'Views',
      render: (row) => <span className="text-zinc-300">{row.views || 0}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      render: (row) => (
        <AdminActionMenu
          actions={[
            { label: 'View Details', icon: Eye, onClick: () => navigate(`/admin/series/${row.id}`) },
            { label: 'View on Site', icon: Eye, onClick: () => window.open(`/comics/${row.id}`, '_blank') },
          ]}
        />
      ),
    },
  ];

  const filters = [
    {
      key: 'status',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'published', label: 'Published' },
        { value: 'draft', label: 'Draft' },
        { value: 'taken_down', label: 'Taken Down' },
      ],
    },
  ];

  return (
    <AdminPageShell
      title="Series Management"
      subtitle={`${total} series`}
      icon={BookOpen}
      headerColor="from-green-600 to-teal-600"
      loading={loading && series.length === 0}
      error={error}
      isAuthError={isAuthError}
      onRetry={refresh}
    >
      <div className="space-y-6">
        <AdminFilters
          filters={filters}
          values={{ status: params.status || 'all' }}
          onChange={(key, val) => setFilter(key, val === 'all' ? '' : val)}
          searchValue={search}
          onSearchChange={setSearch}
          onSearch={() => setFilter('search', search)}
          searchPlaceholder="Search series..."
        />

        {isEmpty ? (
          <AdminEmptyState
            title="No series found"
            message="No series to display."
            icon={BookOpen}
          />
        ) : (
          <>
            <AdminTable
              columns={columns}
              data={series}
              onRowClick={(row) => navigate(`/admin/series/${row.id}`)}
            />
            {totalPages > 1 && (
              <AdminPagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            )}
          </>
        )}
      </div>
    </AdminPageShell>
  );
};

// ===========================
// ADMIN ARTWORKS LIST
// ===========================
export const AdminArtworksList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const {
    data: artworks,
    total,
    loading,
    error,
    isAuthError,
    page,
    limit,
    totalPages,
    setPage,
    setLimit,
    setFilter,
    params,
    refresh,
    isEmpty,
  } = useAdminList('artworks', { status: '' });

  const columns = [
    {
      key: 'artwork',
      header: 'Artwork',
      render: (row) => (
        <div className="flex items-center gap-3">
          {(row.thumbnail_url || row.image_url) ? (
            <img src={row.thumbnail_url || row.image_url} alt="" className="w-12 h-12 rounded object-cover" />
          ) : (
            <div className="w-12 h-12 rounded bg-zinc-700 flex items-center justify-center">
              <Image className="w-5 h-5 text-zinc-500" />
            </div>
          )}
          <div>
            <div className="font-medium text-white">{row.title || 'Untitled'}</div>
            <div className="text-sm text-zinc-400">by {row.artist_name || 'Unknown'}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant="outline" className={
          row.status === 'taken_down' ? 'bg-red-500/20 text-red-400 border-red-500/50' :
          'bg-green-500/20 text-green-400 border-green-500/50'
        }>
          {row.status || 'active'}
        </Badge>
      ),
    },
    {
      key: 'views',
      header: 'Views',
      render: (row) => <span className="text-zinc-300">{row.views || 0}</span>,
    },
    {
      key: 'likes',
      header: 'Likes',
      render: (row) => <span className="text-zinc-300">{row.likes || 0}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      render: (row) => (
        <AdminActionMenu
          actions={[
            { label: 'View Details', icon: Eye, onClick: () => navigate(`/admin/artworks/${row.id}`) },
          ]}
        />
      ),
    },
  ];

  return (
    <AdminPageShell
      title="Artwork Management"
      subtitle={`${total} artworks`}
      icon={Image}
      headerColor="from-pink-600 to-rose-600"
      loading={loading && artworks.length === 0}
      error={error}
      isAuthError={isAuthError}
      onRetry={refresh}
    >
      <div className="space-y-6">
        <AdminFilters
          filters={[
            {
              key: 'status',
              options: [
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'taken_down', label: 'Taken Down' },
              ],
            },
          ]}
          values={{ status: params.status || 'all' }}
          onChange={(key, val) => setFilter(key, val === 'all' ? '' : val)}
          searchValue={search}
          onSearchChange={setSearch}
          onSearch={() => setFilter('search', search)}
          searchPlaceholder="Search artworks..."
        />

        {isEmpty ? (
          <AdminEmptyState
            title="No artworks found"
            message="No artworks to display."
            icon={Image}
          />
        ) : (
          <>
            <AdminTable
              columns={columns}
              data={artworks}
              onRowClick={(row) => navigate(`/admin/artworks/${row.id}`)}
            />
            {totalPages > 1 && (
              <AdminPagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            )}
          </>
        )}
      </div>
    </AdminPageShell>
  );
};

// ===========================
// ADMIN REPORTS LIST
// ===========================
export const AdminReportsList = () => {
  const navigate = useNavigate();

  const {
    data: reports,
    total,
    loading,
    error,
    isAuthError,
    page,
    limit,
    totalPages,
    setPage,
    setLimit,
    setFilter,
    params,
    refresh,
    isEmpty,
  } = useAdminList('reports', { status: 'pending' });

  const handleResolve = async (reportId, action) => {
    const reason = action === 'dismiss' ? '' : prompt('Enter action reason (optional):');
    const result = await adminPost(`/admin/reports/${reportId}/resolve`, { action, reason });
    if (result.ok) {
      toast.success(`Report ${action}ed`);
      refresh();
    } else {
      toast.error(result.error);
    }
  };

  const columns = [
    {
      key: 'content',
      header: 'Reported Content',
      render: (row) => (
        <div>
          <div className="font-medium text-white">{row.content_title || 'Unknown Content'}</div>
          <div className="text-sm text-zinc-400">{row.content_type}</div>
        </div>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (row) => (
        <div className="max-w-xs truncate text-zinc-300">{row.reason}</div>
      ),
    },
    {
      key: 'reporter',
      header: 'Reporter',
      render: (row) => (
        <span className="text-zinc-400">{row.reporter_name || row.reporter_email}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant="outline" className={
          row.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' :
          row.status === 'actioned' ? 'bg-green-500/20 text-green-400 border-green-500/50' :
          'bg-zinc-500/20 text-zinc-400 border-zinc-500/50'
        }>
          {row.status}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      render: (row) => row.status === 'pending' ? (
        <AdminActionMenu
          actions={[
            { label: 'Take Action', icon: AlertTriangle, variant: 'danger', onClick: () => handleResolve(row.id, 'action') },
            { label: 'Dismiss', icon: XCircle, onClick: () => handleResolve(row.id, 'dismiss') },
          ]}
        />
      ) : null,
    },
  ];

  return (
    <AdminPageShell
      title="Content Reports"
      subtitle={`${total} reports`}
      icon={Flag}
      headerColor="from-red-600 to-orange-600"
      loading={loading && reports.length === 0}
      error={error}
      isAuthError={isAuthError}
      onRetry={refresh}
    >
      <div className="space-y-6">
        <AdminFilters
          filters={[
            {
              key: 'status',
              options: [
                { value: 'all', label: 'All Reports' },
                { value: 'pending', label: 'Pending' },
                { value: 'actioned', label: 'Actioned' },
                { value: 'dismissed', label: 'Dismissed' },
              ],
            },
          ]}
          values={{ status: params.status || 'all' }}
          onChange={(key, val) => setFilter(key, val === 'all' ? '' : val)}
        />

        {isEmpty ? (
          <AdminEmptyState
            title="No reports found"
            message={params.status === 'pending' ? "No pending reports. Great job!" : "No reports match your filters."}
            icon={Flag}
          />
        ) : (
          <>
            <AdminTable
              columns={columns}
              data={reports}
            />
            {totalPages > 1 && (
              <AdminPagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            )}
          </>
        )}
      </div>
    </AdminPageShell>
  );
};

// ===========================
// ADMIN NOVELS LIST
// ===========================
export const AdminNovelsList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const {
    data: novels,
    total,
    loading,
    error,
    isAuthError,
    page,
    limit,
    totalPages,
    setPage,
    setLimit,
    setFilter,
    params,
    refresh,
    isEmpty,
  } = useAdminList('novels', { status: '' });

  const columns = [
    {
      key: 'novel',
      header: 'Novel',
      render: (row) => (
        <div className="flex items-center gap-3">
          {row.cover_image_url ? (
            <img src={row.cover_image_url} alt="" className="w-12 h-16 rounded object-cover" />
          ) : (
            <div className="w-12 h-16 rounded bg-zinc-700 flex items-center justify-center">
              <FileText className="w-5 h-5 text-zinc-500" />
            </div>
          )}
          <div>
            <div className="font-medium text-white">{row.title}</div>
            <div className="text-sm text-zinc-400">by {row.author_name || 'Unknown'}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant="outline" className={
          row.status === 'published' ? 'bg-green-500/20 text-green-400 border-green-500/50' :
          row.status === 'draft' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' :
          'bg-zinc-500/20 text-zinc-400 border-zinc-500/50'
        }>
          {row.status || 'draft'}
        </Badge>
      ),
    },
    {
      key: 'chapters',
      header: 'Chapters',
      render: (row) => <span className="text-zinc-300">{row.chapter_count || 0}</span>,
    },
    {
      key: 'views',
      header: 'Views',
      render: (row) => <span className="text-zinc-300">{row.views || 0}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      render: (row) => (
        <AdminActionMenu
          actions={[
            { label: 'View Details', icon: Eye, onClick: () => navigate(`/admin/novels/${row.id}`) },
          ]}
        />
      ),
    },
  ];

  return (
    <AdminPageShell
      title="Novel Management"
      subtitle={`${total} novels`}
      icon={FileText}
      headerColor="from-indigo-600 to-purple-600"
      loading={loading && novels.length === 0}
      error={error}
      isAuthError={isAuthError}
      onRetry={refresh}
    >
      <div className="space-y-6">
        <AdminFilters
          filters={[
            {
              key: 'status',
              options: [
                { value: 'all', label: 'All Status' },
                { value: 'published', label: 'Published' },
                { value: 'draft', label: 'Draft' },
              ],
            },
          ]}
          values={{ status: params.status || 'all' }}
          onChange={(key, val) => setFilter(key, val === 'all' ? '' : val)}
          searchValue={search}
          onSearchChange={setSearch}
          onSearch={() => setFilter('search', search)}
          searchPlaceholder="Search novels..."
        />

        {isEmpty ? (
          <AdminEmptyState
            title="No novels found"
            message="No novels to display."
            icon={FileText}
          />
        ) : (
          <>
            <AdminTable
              columns={columns}
              data={novels}
              onRowClick={(row) => navigate(`/admin/novels/${row.id}`)}
            />
            {totalPages > 1 && (
              <AdminPagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            )}
          </>
        )}
      </div>
    </AdminPageShell>
  );
};

// ===========================
// ADMIN AUDIT LOGS
// ===========================
export const AdminAuditLogs = () => {
  const {
    data: logs,
    total,
    loading,
    error,
    isAuthError,
    page,
    limit,
    totalPages,
    setPage,
    setLimit,
    setFilter,
    params,
    refresh,
    isEmpty,
  } = useAdminList('auditLogs', { action: '' });

  const columns = [
    {
      key: 'timestamp',
      header: 'Time',
      render: (row) => (
        <span className="text-zinc-400 text-sm">
          {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
        </span>
      ),
    },
    {
      key: 'admin',
      header: 'Admin',
      render: (row) => (
        <span className="text-zinc-300">{row.actor_email || row.actor_admin_id}</span>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (row) => (
        <Badge variant="outline" className="bg-purple-500/20 text-purple-400 border-purple-500/50">
          {row.action}
        </Badge>
      ),
    },
    {
      key: 'target',
      header: 'Target',
      render: (row) => (
        <div className="text-sm">
          <span className="text-zinc-400">{row.target_type}: </span>
          <span className="text-zinc-300">{row.target_id}</span>
        </div>
      ),
    },
    {
      key: 'details',
      header: 'Details',
      render: (row) => (
        <div className="max-w-xs truncate text-zinc-500 text-sm">
          {row.metadata ? JSON.stringify(row.metadata).slice(0, 50) : '—'}
        </div>
      ),
    },
  ];

  return (
    <AdminPageShell
      title="Audit Logs"
      subtitle={`${total} entries`}
      icon={History}
      headerColor="from-zinc-600 to-zinc-700"
      loading={loading && logs.length === 0}
      error={error}
      isAuthError={isAuthError}
      onRetry={refresh}
    >
      <div className="space-y-6">
        <AdminFilters
          filters={[
            {
              key: 'action',
              options: [
                { value: 'all', label: 'All Actions' },
                { value: 'user_banned', label: 'User Banned' },
                { value: 'content_removed', label: 'Content Removed' },
                { value: 'verification_approved', label: 'Verification Approved' },
              ],
            },
          ]}
          values={{ action: params.action || 'all' }}
          onChange={(key, val) => setFilter(key, val === 'all' ? '' : val)}
        />

        {isEmpty ? (
          <AdminEmptyState
            title="No audit logs"
            message="No audit logs to display."
            icon={History}
          />
        ) : (
          <>
            <AdminTable
              columns={columns}
              data={logs}
            />
            {totalPages > 1 && (
              <AdminPagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            )}
          </>
        )}
      </div>
    </AdminPageShell>
  );
};

export default {
  AdminUsersList,
  AdminCreatorsList,
  AdminSeriesList,
  AdminArtworksList,
  AdminReportsList,
  AdminNovelsList,
  AdminAuditLogs,
};
